import React from 'react';
import { useSelector } from 'react-redux';
import { useWindowSize } from 'react-use';
import cn from 'classnames';

import { fromMetricTypeToKpiUnit } from 'bl/kpi/fromMetricTypeToKpiUnit.bl';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import Icon from 'components/generic/Icon.component';
import Tooltip from 'components/generic/Tooltip.component';
import TruncatedText from 'components/generic/TruncatedText.component';
import ScoreAmount from 'components/kpi/ScoreAmount.component';
import TooltipMetricTextForecast from 'components/kpi/TooltipMetricTextForecast.component';
import TooltipMetricTextPast from 'components/kpi/TooltipMetricTextPast.component';
import CurrencySign from 'components/price/CurrencySign.component';
import MOBILE_VIEWPORT from 'constants/ui/mobileViewport.const';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import { selectCurrentSalonCountryCode } from 'selectors/currentSalon.selectors';
import logger from 'utils/logger.util';
import isFullArray from 'utils/predicates/array/isFullArray.util';

import CN from './ScoreItem.module.scss';

const L = logger('ScoreItem'); // eslint-disable-line no-unused-vars
const MAX_THREE_DIGIT = 100;

const getTooltipText = (metricKey, isForecast, t) => {
  const cleanMetricKey = metricKey.split('|')[0]; // NOTE: we will only use the part before '|'-> revenue_per_category|77609
  const tooltipTextSource = isForecast ? <TooltipMetricTextForecast metric={cleanMetricKey} /> : <TooltipMetricTextPast metric={cleanMetricKey} />;
  return tooltipTextSource || t('staffKpisReports.kpiReports.defaultText');
};

const TooltipContent = ({ metricKey, isForecast }) => {
  const { t } = useTranslation();

  return (
    <div data-bem={isForecast ? 'TooltipContentForecast' : 'TooltipContentPast'} className={CN.textTooltip}>
      {getTooltipText(metricKey, isForecast, t)}
    </div>
  );
};

const TooltipDataContent = ({ tooltipData }) => (
  <div data-bem="TooltipDataContent" className={CN.textTooltipExtra}>
    {tooltipData.map((data, index) => {
      const { title, type, value } = data ?? {};

      if (!Number.isFinite(value) || value === 0 || value === 0.0) return null;

      const displayValue = type === 'money' ? <CurrencySign value={value} /> : value;

      return (
        <div key={index}>
          {title}: {displayValue}
        </div>
      );
    })}
  </div>
);

const hasNonZeroValues = (tooltipData) => {
  if (!isFullArray(tooltipData)) return false;
  return tooltipData.some((item) => Number.isFinite(item.value) && item.value !== 0 && item.value !== 0.0);
};

const ScoreItem = ({ value, isForecast, isFirst }) => {
  // eslint-disable-next-line no-unused-vars
  const { metricType, metricKey, order, label, performedValue, tooltipData } = value ?? {};
  const countryCode = useSelector(selectCurrentSalonCountryCode);

  const { isDarkMode } = useTheme();
  const { width: windowWidth } = useWindowSize();
  const isMobileViewport = windowWidth <= MOBILE_VIEWPORT;
  const tooltipSize = isMobileViewport && performedValue >= MAX_THREE_DIGIT ? 'sm' : 'auto';
  const tooltipVariant = isMobileViewport && isFirst ? 'slickBottomToLeft' : isMobileViewport || performedValue >= MAX_THREE_DIGIT ? 'slickTopToLeft' : 'slickLeft';

  const { val: renderModal, yay: showModal, nay: hideModal } = useTip(false);

  L.debug('()', value);

  const renderedScoreAmount = <ScoreAmount unit={metricType} value={performedValue} />;
  const renderedTooltipDataTooltip = <TooltipDataContent tooltipData={tooltipData} />;

  const shouldShowTooltip = hasNonZeroValues(tooltipData);

  const metricIconVariant = fromMetricTypeToKpiUnit(metricType, countryCode);

  return (
    <div
      data-bem="ScoreItem"
      className={cn({
        [CN.component]: true, // default UI - blue
        [CN.dark]: isDarkMode,
        [CN.isForecast]: isForecast, //  UI - forecast
        [CN.noData]: !performedValue, // if item value is null empty undefined ...? BUT not 0$ or 0%...
      })}
    >
      <div className={CN.flex}>
        <Button onClick={showModal} variant="icon" width="xs">
          <Icon variant={metricIconVariant} width="lg" />
        </Button>
        <div className={CN.text}>
          <TruncatedText max={70} suffix="..." value={label} />
        </div>
      </div>

      <div>
        {tooltipData && shouldShowTooltip ? (
          <Tooltip content={renderedTooltipDataTooltip} variant={tooltipVariant} width={tooltipSize}>
            <div className={CN.flex}>
              <div className={CN.iconContainer}>
                <Icon variant="info" width="sm" />
              </div>

              {renderedScoreAmount}
            </div>
          </Tooltip>
        ) : (
          renderedScoreAmount
        )}
      </div>
      <SalonModal tag="ScoreItem__modal--metric" isOpen={renderModal} variant="mediumWithHeaderOnly">
        <SalonModal.Header>
          {value?.label}
          <Button variant="empty" onClick={hideModal}>
            <Icon variant="close" />
          </Button>
        </SalonModal.Header>
        <SalonModal.Body>
          <div className={CN.modalContainer}>
            <TooltipContent metricKey={metricKey} isForecast={isForecast} />
          </div>
        </SalonModal.Body>
      </SalonModal>
    </div>
  );
};

export default ScoreItem;
